import { c as _c } from "react/compiler-runtime";
import type { AccrualPosition, MarketParams } from '@morpho-org/blue-sdk';
import type { VaultV2BlueReallocation } from '@morpho-org/morpho-sdk';
import { ZERO } from '@morpho-org/dapp-core';
import { useQuery } from '@tanstack/react-query';
import type { TransactionBuilder } from '@/contexts/Transaction/TransactionV2.context';
import { useWeb3Context } from '@/contexts/Web3Context';
import { assertBlueBundlesV1Deployed, assertBuilderIsSigner, getMarketActionDeadline, MARKET_ACTION_REBUILD_INTERVAL_MS } from './marketAction.helpers';
import { useMarketBlue } from './useMarketBlue';
export interface UseMarketRefinanceBuilderParams {
  /** Source market params (the position being refinanced away from). */
  sourceMarketParams: MarketParams | undefined;
  /** Target market params (must share both loan + collateral tokens). */
  targetMarketParams: MarketParams | undefined;
  /**
   * Public-allocator reallocations into the destination market, sized to the full migrated debt;
   * passed through to v6 `refinance` as `reallocations`.
   */
  targetReallocations?: readonly VaultV2BlueReallocation[];
}

/**
 * Builds the atomic cross-market `market.refinance(...)` action (morpho-sdk v6 BlueBundlesV1),
 * the refinance sister of {@link useMarketActionBuilder}. Binds BOTH the source and
 * target markets to the connected client via {@link useMarketBlue} (so both accrual
 * positions are read at the same shared snapshot block), then builds
 * `sourceMarket.refinance({ userAddress, positionData, destination, reallocations, deadline })`
 * + its `getRequirements()` (a single global `setAuthorization` covers both markets)
 * in ONE query so the built `transaction` and its `requirements` are always a
 * consistent pair (see the desync note in `useMarketActionBuilder`).
 *
 * v6 migrates the FULL live source position (debt + collateral) — there is no partial or
 * collateral-only mode, so there are no amount inputs: the action is fully determined by the two
 * markets and the live source position.
 *
 * Returns the unsigned `transaction` builder + `requirements` for `submitTransactions`,
 * plus BOTH positions for the from/to recap.
 */
export function useMarketRefinanceBuilder(t0) {
  const $ = _c(46);
  const {
    sourceMarketParams,
    targetMarketParams,
    targetReallocations
  } = t0;
  const {
    account,
    viemClient,
    targetChainId
  } = useWeb3Context();
  const {
    market: sourceMarket,
    positionData: sourcePositionData,
    isLoading: isSourceLoading,
    error: sourceError
  } = useMarketBlue(sourceMarketParams);
  const {
    positionData: targetPositionData,
    isLoading: isTargetLoading,
    error: targetError
  } = useMarketBlue(targetMarketParams);
  const isPreparedEnabled = !!sourceMarket && !!sourceMarketParams && !!targetMarketParams && !!account.address && !!sourcePositionData && !!targetPositionData && (sourcePositionData.borrowShares > ZERO || sourcePositionData.collateral > ZERO);
  const t1 = sourceMarketParams?.id;
  const t2 = targetMarketParams?.id;
  let t3;
  if ($[0] !== account.address || $[1] !== sourcePositionData || $[2] !== t1 || $[3] !== t2 || $[4] !== targetChainId || $[5] !== targetPositionData || $[6] !== targetReallocations || $[7] !== viemClient) {
    t3 = ["market-refinance-prepared", t1, t2, account.address, targetReallocations, sourcePositionData, targetPositionData, targetChainId, viemClient];
    $[0] = account.address;
    $[1] = sourcePositionData;
    $[2] = t1;
    $[3] = t2;
    $[4] = targetChainId;
    $[5] = targetPositionData;
    $[6] = targetReallocations;
    $[7] = viemClient;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== account.address || $[10] !== sourceMarket || $[11] !== sourceMarketParams || $[12] !== sourcePositionData || $[13] !== targetChainId || $[14] !== targetMarketParams || $[15] !== targetPositionData || $[16] !== targetReallocations || $[17] !== viemClient) {
    t4 = async () => {
      assertBuilderIsSigner({
        userAddress: account.address,
        accountAddress: account.address,
        clientChainId: viemClient.chain?.id,
        expectedChainId: targetChainId
      });
      await assertBlueBundlesV1Deployed(viemClient, targetChainId);
      const userAddress = account.address;
      const deadline = getMarketActionDeadline();
      const action = sourceMarket.refinance({
        userAddress,
        positionData: sourcePositionData,
        destination: {
          marketParams: targetMarketParams,
          positionData: targetPositionData
        },
        reallocations: targetReallocations,
        deadline
      });
      const requirements = await action.getRequirements();
      const simulationKey = buildRefinanceSimulationKey({
        sourceMarketId: sourceMarketParams.id,
        targetMarketId: targetMarketParams.id,
        sourcePositionData,
        targetPositionData,
        targetReallocations
      });
      return {
        action,
        requirements,
        simulationKey,
        deadline
      };
    };
    $[9] = account.address;
    $[10] = sourceMarket;
    $[11] = sourceMarketParams;
    $[12] = sourcePositionData;
    $[13] = targetChainId;
    $[14] = targetMarketParams;
    $[15] = targetPositionData;
    $[16] = targetReallocations;
    $[17] = viemClient;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== account.address || $[20] !== sourceMarketParams?.id || $[21] !== targetChainId || $[22] !== targetMarketParams?.id || $[23] !== viemClient) {
    t5 = (previousData, previousQuery) => {
      if (previousData == null || !previousQuery) {
        return;
      }
      const [, prevSourceId, prevTargetId, prevAccount] = previousQuery.queryKey;
      const prevChainId = previousQuery.queryKey.at(-2);
      const prevClient = previousQuery.queryKey.at(-1);
      return prevSourceId === sourceMarketParams?.id && prevTargetId === targetMarketParams?.id && prevAccount === account.address && prevChainId === targetChainId && prevClient === viemClient ? previousData : undefined;
    };
    $[19] = account.address;
    $[20] = sourceMarketParams?.id;
    $[21] = targetChainId;
    $[22] = targetMarketParams?.id;
    $[23] = viemClient;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[25] !== isPreparedEnabled || $[26] !== t3 || $[27] !== t4 || $[28] !== t5) {
    t6 = {
      queryKey: t3,
      queryFn: t4,
      enabled: isPreparedEnabled,
      refetchInterval: MARKET_ACTION_REBUILD_INTERVAL_MS,
      refetchIntervalInBackground: true,
      placeholderData: t5
    };
    $[25] = isPreparedEnabled;
    $[26] = t3;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  const {
    data: prepared,
    isLoading: isActionLoading,
    error: buildError,
    isPlaceholderData,
    isFetching,
    refetch
  } = useQuery(t6);
  const exposedPrepared = isPreparedEnabled ? prepared : undefined;
  const action_0 = exposedPrepared?.action ?? null;
  const areRequirementsReady = !isPreparedEnabled || prepared !== undefined;
  const t7 = (action_0?.buildTx ? action_0 : undefined) as TransactionBuilder | undefined;
  const t8 = exposedPrepared?.requirements;
  const t9 = areRequirementsReady && !isPlaceholderData;
  const t10 = exposedPrepared?.simulationKey;
  const t11 = exposedPrepared?.deadline;
  const t12 = sourceError ?? targetError ?? null;
  const t13 = isSourceLoading || isTargetLoading;
  const t14 = isSourceLoading || isTargetLoading || isActionLoading;
  let t15;
  if ($[30] !== buildError || $[31] !== isFetching || $[32] !== isPlaceholderData || $[33] !== isSourceLoading || $[34] !== refetch || $[35] !== sourcePositionData || $[36] !== t10 || $[37] !== t11 || $[38] !== t12 || $[39] !== t13 || $[40] !== t14 || $[41] !== t7 || $[42] !== t8 || $[43] !== t9 || $[44] !== targetPositionData) {
    t15 = {
      transaction: t7,
      requirements: t8,
      areRequirementsReady: t9,
      isRequirementsPlaceholderData: isPlaceholderData,
      simulationKey: t10,
      deadline: t11,
      rebuild: refetch,
      isRefreshing: isFetching,
      sourcePositionData,
      targetPositionData,
      marketError: t12,
      buildError,
      isPositionLoading: t13,
      isSourceLoading,
      isLoading: t14
    };
    $[30] = buildError;
    $[31] = isFetching;
    $[32] = isPlaceholderData;
    $[33] = isSourceLoading;
    $[34] = refetch;
    $[35] = sourcePositionData;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
    $[39] = t13;
    $[40] = t14;
    $[41] = t7;
    $[42] = t8;
    $[43] = t9;
    $[44] = targetPositionData;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  return t15;
}

/**
 * Stable content signature of a built refinance action — every input that shapes the signed
 * calldata (both market ids, both positions' accrual state, reallocations). The full migrated
 * amounts are captured by the source position signature, since v6 moves the entire live position.
 * Keyed like `buildMarketActionSimulationKey` (NOT raw calldata, which carries a churning wall-clock
 * `metadata.timestamp`), and stamped atomically with the `action` it describes.
 */
function buildRefinanceSimulationKey({
  sourceMarketId,
  targetMarketId,
  sourcePositionData,
  targetPositionData,
  targetReallocations
}: {
  sourceMarketId: string;
  targetMarketId: string;
  sourcePositionData: AccrualPosition | undefined;
  targetPositionData: AccrualPosition | undefined;
  targetReallocations: readonly VaultV2BlueReallocation[] | undefined;
}): string {
  const positionSignature = (position: AccrualPosition | undefined) => position ? [position.collateral, position.borrowShares, position.market.totalBorrowAssets, position.market.totalBorrowShares, position.market.totalSupplyAssets, position.market.totalSupplyShares, position.market.lastUpdate, position.market.price ?? 0n].map(String).join(':') : 'no-position';
  const reallocationsSignature = (targetReallocations ?? []).map(({
    vault,
    assets,
    penalty
  }) => `${vault}:${assets}:${penalty}`).join('|');
  return [sourceMarketId, targetMarketId, positionSignature(sourcePositionData), positionSignature(targetPositionData), reallocationsSignature].join('|');
}